import { useEffect, useMemo, useRef } from 'react';
import { useFetcher } from '@remix-run/react';
import { atom, useAtom } from 'jotai';
import {
    CHAT_LINK_PREVIEW_API,
    type loader,
} from '~/routes/remix-api.chat.link-preview';
import type { LinkPreview } from '../types';

/**
 * Cache resolved link previews by URL to avoid duplicate API requests.
 */
const linkPreviewCacheAtom = atom<Record<string, LinkPreview | null>>({});

/**
 * Hook to fetch chat message link previews with cache support.
 */
export function useLinkPreviews(
    urls: string[],
    disableFetch: boolean = false,
): LinkPreview[] {
    const [cache, setCache] = useAtom(linkPreviewCacheAtom);
    const fetcher = useFetcher<typeof loader>();
    const fetchingUrls = useRef<string[]>([]);
    const uniqueUrls = useMemo(() => {
        return Array.from(new Set(urls));
    }, [urls]);
    const uncachedUrls = useMemo(() => {
        // `null` values are URLs that failed to resolve, do not treat as cache miss.
        return uniqueUrls.filter((url) => cache[url] === undefined);
    }, [cache, uniqueUrls]);

    // Fetch link previews for uncached URLs.
    useEffect(() => {
        if (
            !uncachedUrls.length ||
            fetchingUrls.current.length ||
            fetcher.state !== 'idle' ||
            disableFetch
        ) {
            return;
        }
        // Fetch up to 10 URLs at a time to prevent URL length limits.
        const urlsToFetch = uncachedUrls.slice(0, 10);
        const url = new URL(CHAT_LINK_PREVIEW_API, window.location.origin);
        for (const uncachedUrl of urlsToFetch) {
            url.searchParams.append('url', uncachedUrl);
        }
        fetcher.load(`${url.pathname}${url.search}`);
        fetchingUrls.current = [...urlsToFetch];
    }, [fetcher, uncachedUrls, disableFetch]);

    // Update the cache with the fetched link previews.
    useEffect(() => {
        if (fetcher.data === undefined || fetcher.state !== 'idle') {
            return;
        }
        if (fetcher.data === null) {
            // Failed requests are `null` to prevent repeated requests.
            setCache((prev) => {
                const next = { ...prev };
                for (const url of fetchingUrls.current) {
                    next[url] = null;
                }
                return next;
            });
            fetchingUrls.current = [];
            return;
        }
        const previews = fetcher.data.previews;
        setCache((prev) => {
            const next = { ...prev };
            for (const [url, preview] of Object.entries(previews)) {
                // Failed resolutions are `null` to prevent repeated requests.
                next[url] = preview ?? null;
            }
            return next;
        });
        fetchingUrls.current = [];
    }, [fetcher.data, fetcher.state, setCache]);

    return useMemo(() => {
        return uniqueUrls
            .map((url) => cache[url])
            .filter((preview): preview is LinkPreview => !!preview);
    }, [cache, uniqueUrls]);
}
